import React, { useState, FormEvent, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthContext';
import { useApi } from '../lib/apiClient';
import { FeatureErrorHandlers } from '../utils/contextualFeatureMessages';
import {
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import {
    Input,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
    Skeleton,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@pagespeedify/ui";
import { toast } from "sonner";
import { ReportsHistory } from '../components/ReportsHistory';
import { ReportGenerationStatus } from '../components/reports/ReportGenerationStatus';
import { ReportUsageResponse, CreateReportResponseData, ReportStatus } from '../types/reports';
import { useTour } from '../context/TourContext';
import ReportsTour from '../components/tour/ReportsTour';

// --- UsageInfoDisplay Component (assumed correct) ---
interface UsageInfoDisplayProps {
    usage: ReportUsageResponse | null | undefined;
    isLoading: boolean;
    error: Error | null;
}

const UsageInfoDisplay: React.FC<UsageInfoDisplayProps> = ({ usage, isLoading, error }) => {
    if (isLoading) {
        return (
            <div className="text-sm text-gray-500 dark:text-gray-400">
                <Skeleton className="h-4 w-48" />
            </div>
        );
    }
    if (!usage || error) {
        return <p className="text-sm text-gray-500 dark:text-gray-400">Usage data unavailable.</p>;
    }

    return (
        <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
            <span>Used: {usage.consumedInPeriod}/{usage.limitForPeriod}</span>
            <span className="ml-2">Remaining: {usage.remainingInBundle}</span>
            {usage.overageCostPerUnit !== null && usage.overageCostPerUnit > 0 && (
                <span className="ml-2">Overlimit: {usage.overageCostPerUnit} credits/report</span>
            )}
        </div>
    );
};


export default function ReportsPage() {
    const { user, isLoading: authIsLoading, refreshUser } = useAuth();
    const apiClient = useApi();
    const queryClient = useQueryClient();
    const location = useLocation();
    const { startPageTour, isPageTourSeen } = useTour();

    const [urlToTest, setUrlToTest] = useState<string>("");
    const [device, setDevice] = useState<string>("mobile"); // Added device state
    const [apiError, setApiError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [generatingReportId, setGeneratingReportId] = useState<string | null>(null);

    // Check for generating report ID passed via navigation state (from regenerate)
    useEffect(() => {
        const state = location.state as { generatingReportId?: string } | null;
        if (state?.generatingReportId) {
            setGeneratingReportId(state.generatingReportId);
            // Clear the state to prevent re-triggering
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [location.state]);

    // Read URL parameters and initialize form state (for Ask AI flow from homepage)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlParam = searchParams.get('url');

        if (urlParam) {
            setUrlToTest(decodeURIComponent(urlParam));
        }
    }, [location.search]);

    // Check for an ongoing report generation when the component mounts
    const { isLoading: isCheckingInitialStatus } = useQuery<ReportStatus | null, Error>({
        queryKey: ['initialReportGenerationStatus', user?.uid],
        queryFn: async () => {
            if (!apiClient) return null;
            try {
                // This endpoint gets the status of the *last* initiated report
                const status = await apiClient.get<ReportStatus>(`/reports/status`);
                if (status && (status.status === 'PENDING' || status.status === 'RUNNING') && status.reportId) {
                    setGeneratingReportId(status.reportId);
                }
                return status;
            } catch (error: any) {
                if (error.response?.status === 404) {
                    return null; // No status file, which is normal.
                }
                // Don't show an error toast for this initial check
                console.error("Error checking initial report status:", error);
                return null;
            }
        },
        enabled: !!user && !authIsLoading && !!apiClient && !generatingReportId,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const {
        data: reportUsageContext,
        isLoading: isUsageLoading,
        error: usageError,
    } = useQuery<ReportUsageResponse, Error>({
        queryKey: ['reportUsage', user?.uid],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            const response = await apiClient.get<{ success: boolean, usage: ReportUsageResponse, message?: string }>('/reports/usage');
            if (!response.success || !response.usage) {
                throw new Error(response.message || 'Failed to fetch usage data.');
            }
            return response.usage;
        },
        enabled: !!user && !authIsLoading && !!apiClient,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });



    // Start reports tour if not seen yet
    useEffect(() => {
        if (user && !isPageTourSeen('reports')) {
            setTimeout(() => startPageTour('reports'), 1000);
        }
    }, [user, startPageTour, isPageTourSeen]);



    const handleCreateReportSubmit = async (event: FormEvent) => {
        event.preventDefault();

        // Check canCreate before any processing
        if (!reportUsageContext?.canCreate?.reports) {
            const reason = reportUsageContext?.canCreate?.reportsReason || 'Access denied for reports';
            toast.error(reason);
            return;
        }

        if (!urlToTest.trim()) {
            toast.error("Please enter a URL to test.");
            return;
        }

        // Enhanced URL Validation
        const commonNonHtmlExtensions = [
            // Common documents & text
            '.txt', '.md', '.pdf', '.doc', '.docx', '.odt', '.rtf', '.tex', '.wpd', '.csv', '.json', '.xml', '.yaml', '.yml',
            // Code & scripts (often served as plain text or specific non-HTML mime types)
            '.js', '.ts', '.jsx', '.tsx', '.css', '.scss', '.less', '.py', '.java', '.c', '.cpp', '.cs', '.go', '.php', '.rb', '.sh', '.ps1', '.sql',
            // Images
            '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.ico', '.tiff', '.heic',
            // Audio
            '.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a',
            // Video
            '.mp4', '.mov', '.avi', '.wmv', '.mkv', '.flv', '.webm',
            // Archives
            '.zip', '.tar', '.gz', '.rar', '.7z', '.bz2',
            // Other raw/data formats
            '.raw', '.dat', '.bin', '.log', '.bak', '.tmp', '.ics', '.vcf'
        ];
        const lowercasedUrl = urlToTest.toLowerCase();

        if (lowercasedUrl.includes('raw.githubusercontent.com')) {
            toast.error("URLs from raw.githubusercontent.com are not allowed as they typically represent raw files, not webpages.");
            return;
        }

        // YouTube video page specific validation
        const youtubeVideoPatterns = [
            'youtube.com/watch?v=',
            'youtu.be/'
        ];
        if (youtubeVideoPatterns.some(pattern => lowercasedUrl.includes(pattern))) {
            toast.error("Direct analysis of individual YouTube video pages is not supported at this time due to potential processing issues. Please try a different URL.");
            return;
        }

        if (commonNonHtmlExtensions.some(ext => lowercasedUrl.endsWith(ext))) {
            toast.error("The URL must be a webpage. Direct links to files of this type (e.g., images, documents, scripts) are not supported.");
            return;
        }

        if (!user || !apiClient) {
            toast.error("User not found or API client not available. Please try logging in again.");
            return;
        }

        setIsSubmitting(true);
        setApiError(null);

        let hostname;
        try {
            const parsedUrl = new URL(urlToTest.startsWith('http') ? urlToTest : `https://${urlToTest}`);
            hostname = parsedUrl.hostname;
            if (!parsedUrl.protocol.match(/^https?:$/)) {
                toast.error("Invalid URL protocol. Please ensure it starts with http:// or https://");
                setIsSubmitting(false);
                return;
            }
        } catch (urlError) {
            toast.error("Invalid URL format. Please enter a valid URL, including http:// or https://");
            setIsSubmitting(false);
            return;
        }

        toast.info(`Generating report for ${urlToTest}...`, { duration: 2000 });

        const reportPayload = {
            page_url: urlToTest,
            website_url: hostname,
            strategy: device,
            report_data: {
                source: "admin_ui_reports_page_v4.0"
            },
        };

        try {
            const responseData = await apiClient.post<CreateReportResponseData>('/reports', reportPayload);

            if (responseData.success) {
                toast.success(responseData.message || "Report generation initiated!");
                setUrlToTest("");
                queryClient.invalidateQueries({ queryKey: ['reportUsage', user?.uid] });

                // Refresh user data to update header credit balance (for overage charges)
                if (refreshUser) {
                    refreshUser();
                    // Delayed refresh to catch backend processing
                    setTimeout(() => refreshUser(), 2000);
                }

                // The backend now sends `reportId` for async requests, which is the primary mechanism.
                if (responseData.reportId) {
                    setGeneratingReportId(responseData.reportId);
                } else if (responseData.report?.id) {
                    // Fallback for cases where the report is returned synchronously.
                    setGeneratingReportId(responseData.report.id);
                } else {
                    // If no ID is returned on success (unexpected), invalidate the status query as a fallback.
                    // This will cause the useQuery for status to refetch and hopefully find the running report.
                    console.warn("No report ID in success response, forcing status refetch.");
                    queryClient.invalidateQueries({ queryKey: ['initialReportGenerationStatus', user?.uid] });
                }
            } else {
                const reason = responseData.error;
                if (reason) {
                    const friendlyMessage = FeatureErrorHandlers.reports(reason);
                    toast.error(friendlyMessage.title, { description: friendlyMessage.description });
                    setApiError(friendlyMessage.description);
                } else {
                    const message = responseData.message || "Report generation failed.";
                    toast.error('Report Generation Failed', { description: message });
                    setApiError(message);
                }
            }
        } catch (err: any) {
            const reason = err.response?.data?.error;
            if (reason) {
                const friendlyMessage = FeatureErrorHandlers.reports(reason);
                toast.error(friendlyMessage.title, { description: friendlyMessage.description });
                setApiError(friendlyMessage.description);
            } else {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to create report.';
                toast.error('An unexpected error occurred.', { description: errorMessage });
                setApiError(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const getUserPlanText = () => {
        // Log the raw data when the function is called
        // console.log('[ReportsPage] getUserPlanText called. reportUsageContext:', JSON.stringify(reportUsageContext, null, 2) , 'isLoading:', isUsageLoading, 'error:', usageError, 'user:', JSON.stringify(user, null, 2));

        if (isUsageLoading && !reportUsageContext) { // Show skeleton if loading and no data yet
            return <Skeleton className="h-4 w-full max-w-sm" />;
        }

        if (usageError || !reportUsageContext || !reportUsageContext.accessContext || !reportUsageContext.accessContext.planFeatures) {
            // console.warn('[ReportsPage] getUserPlanText: Missing critical data for plan text generation.', { usageError, reportUsageContext });
            return "Plan limits apply. Credit costs for additional reports and AI solutions may vary.";
        }

        // These were used for the old static text display, but are no longer needed
        // since we now display the dynamic validation result component instead

        // The new implementation returns the validation result component
        // which shows real-time cost/limit information
        return (
            <div className="pt-4">
                <div className="text-sm text-muted-foreground">
                    {reportUsageContext && reportUsageContext.remainingInBundle > 0
                        ? `${reportUsageContext.remainingInBundle} reports remaining in your plan`
                        : reportUsageContext?.overageCostPerUnit
                            ? `Reports will cost ${reportUsageContext.overageCostPerUnit} credits each (overage)`
                            : 'Plan limits apply.'
                    }
                </div>
            </div>
        );
    };

    // If a report is generating, show the status component instead of the form.
    if (generatingReportId) {
        return (
            <div className="container mx-auto p-4 md:p-6">
                <ReportGenerationStatus
                    reportId={generatingReportId}
                    onBack={() => {
                        setGeneratingReportId(null);
                        // When user manually goes back, refresh history just in case
                        queryClient.invalidateQueries({ queryKey: ['ReportsHistory'] });
                    }}
                />
                <div className="mt-8">
                    <ReportsHistory />
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-6">
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Performance Reports</CardTitle>
                    <CardDescription>
                        Analyze your website's performance, get actionable insights, and track your progress.
                        {user?.user_type && <span className="ml-2 text-xs uppercase opacity-75">({user.user_type} Account)</span>}
                    </CardDescription>
                </CardHeader>
            </Card>

            <Card className="mb-8" data-tour="generate-report-form">
                <CardHeader>
                    <div className="flex justify-between items-start">
                        <div>
                            <CardTitle>Generate New Report</CardTitle>
                        </div>
                        {(isUsageLoading || (reportUsageContext && !usageError)) && (
                            <div data-tour="report-usage">
                                <UsageInfoDisplay usage={reportUsageContext} isLoading={isUsageLoading} error={usageError} />
                            </div>
                        )}
                        {usageError && !isUsageLoading && (
                            <p className="text-xs text-destructive text-right">Could not load usage data.</p>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleCreateReportSubmit} className="space-y-6">
                        <div className="flex gap-2 items-end">
                            <div className="flex-grow">
                                <Label htmlFor="urlToTest">URL</Label>
                                <Input
                                    id="urlToTest"
                                    type="url"
                                    placeholder="e.g., https://example.com/your-page"
                                    value={urlToTest}
                                    onChange={(e) => setUrlToTest(e.target.value)}
                                    disabled={!reportUsageContext?.canCreate?.reports}
                                    required
                                    className="mt-1 w-full" // Ensure input takes full width of its container
                                />
                            </div>
                            <div className="w-full md:w-[180px] flex-shrink-0">
                                <Label htmlFor="device-select">Device</Label>
                                <Select value={device} onValueChange={setDevice} disabled={!reportUsageContext?.canCreate?.reports}>
                                    <SelectTrigger id="device-select" className="w-full mt-1">
                                        <SelectValue placeholder="Select device" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="mobile">Mobile</SelectItem>
                                        <SelectItem value="desktop">Desktop</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>


                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-4"> {/* Added pt-4 for spacing */}
                            <div className="text-xs text-muted-foreground">
                                {getUserPlanText()}
                            </div>
                            <Button type="submit" disabled={isSubmitting || authIsLoading || !apiClient || isCheckingInitialStatus || !reportUsageContext?.canCreate?.reports} size="lg" className="w-full md:w-auto" data-tour="generate-report-button">
                                {isSubmitting || isCheckingInitialStatus ? "Checking Status..." : "Generate Report"}
                            </Button>
                        </div>
                    </form>
                    {apiError && !isSubmitting && (
                        <p className="mt-4 text-sm text-destructive">Error: {apiError}</p>
                    )}
                </CardContent>
            </Card>

            <ReportsHistory />
            <ReportsTour />
        </div >
    );
}